import React, { useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { UploadSimple, X } from '@phosphor-icons/react'
import { PageContainer } from '../components/layout/PageContainer'
import { Button, Text } from '../components/ui/foundation'
import {
  getUploadErrorMessage,
  isFileDrag,
  parseImageDrop,
} from '../utils/dragDropImages'
import { uploadUserImage, type UserImageAsset } from '../services/userImages'
import { logError, logInfo } from '../utils/logger'
import { stageNewBoardPayload } from '../utils/newBoardIntake'
import { MetaTags } from '../components/seo/MetaTags'

const MAX_IMAGES = 10

const toUploadImageData = (asset: UserImageAsset) => ({
  src: asset.signedUrl,
  source: 'upload' as const,
  storagePath: asset.storagePath,
  originalFileName: asset.originalFileName,
  mimeType: asset.mimeType,
  sizeBytes: asset.sizeBytes,
  uploadedAt: asset.uploadedAt,
  pixelWidth: asset.width,
  pixelHeight: asset.height,
})

export const NewBoardTemplatePage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedImages, setSelectedImages] = useState<UserImageAsset[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isMakingBoard, setIsMakingBoard] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [feedbackTone, setFeedbackTone] = useState<'warning' | 'error'>(
    'warning'
  )

  const remainingSlots = Math.max(0, MAX_IMAGES - selectedImages.length)
  const hasSelectedImages = selectedImages.length > 0
  const canMakeBoard =
    selectedImages.length > 0 && !isUploading && !isMakingBoard
  const inspirationMood = searchParams.get('mood')
  const inspirationPrompt = searchParams.get('prompt')

  const helperCopy = useMemo(() => {
    if (selectedImages.length === 0) return 'Upload 1-10 images to begin.'
    if (selectedImages.length >= MAX_IMAGES)
      return 'Selection full. Remove one to add another.'
    return `${selectedImages.length} selected. Add up to ${remainingSlots} more.`
  }, [remainingSlots, selectedImages.length])

  const uploadFeedback = useMemo(() => {
    if (isUploading) {
      return {
        message: 'Uploading images...',
        className: 'bg-background-100 text-ink-500',
      }
    }

    if (!feedbackMessage) return null

    const className =
      feedbackTone === 'warning'
        ? 'bg-warning-100 text-warning-500'
        : 'bg-error-100 text-error-500'

    return {
      message: feedbackMessage,
      className,
    }
  }, [feedbackMessage, feedbackTone, isUploading])

  const handleUploadFiles = async (incomingFiles: File[]) => {
    if (incomingFiles.length === 0) return
    if (remainingSlots <= 0) {
      setFeedbackTone('warning')
      setFeedbackMessage(`You can add up to ${MAX_IMAGES} images here.`)
      return
    }

    const filesToUpload = incomingFiles.slice(0, remainingSlots)
    const overflowCount = incomingFiles.length - filesToUpload.length

    setIsUploading(true)
    setFeedbackMessage('')

    try {
      const results = await Promise.allSettled(
        filesToUpload.map((file) => uploadUserImage(file))
      )

      const uploaded: UserImageAsset[] = []
      let uploadError = ''

      results.forEach((result) => {
        if (result.status !== 'fulfilled') {
          uploadError = 'Failed to upload image.'
          return
        }

        if (!result.value.success || !result.value.data) {
          uploadError = getUploadErrorMessage(result.value.error)
          return
        }

        uploaded.push(result.value.data)
      })

      if (uploaded.length > 0) {
        setSelectedImages((prev) => [...prev, ...uploaded].slice(0, MAX_IMAGES))
      }

      if (uploadError) {
        setFeedbackTone('error')
        setFeedbackMessage(uploadError)
      } else if (uploaded.length > 0 && overflowCount > 0) {
        setFeedbackTone('warning')
        setFeedbackMessage(
          `Added ${uploaded.length} ${uploaded.length === 1 ? 'image' : 'images'}. ${overflowCount} ${overflowCount === 1 ? 'was' : 'were'} skipped because the limit is ${MAX_IMAGES}.`
        )
      } else if (uploaded.length > 0) {
        setFeedbackMessage('')
      } else if (overflowCount > 0) {
        setFeedbackTone('warning')
        setFeedbackMessage(`You can add up to ${MAX_IMAGES} images here.`)
      }
    } catch (error) {
      logError('NewBoardTemplatePage', 'Unexpected upload failure', { error })
      setFeedbackTone('error')
      setFeedbackMessage('Failed to upload image.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files ?? [])
    await handleUploadFiles(files)
    event.target.value = ''
  }

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragActive(false)

    const { files, rejected } = parseImageDrop(event.dataTransfer)
    if (rejected.length > 0) {
      setFeedbackTone('error')
      setFeedbackMessage('Only JPG and PNG are supported.')
    }

    await handleUploadFiles(files)
  }

  const handleMakeBoard = () => {
    if (!canMakeBoard) return

    const boardId = crypto.randomUUID()
    stageNewBoardPayload(boardId, {
      images: selectedImages.map(toUploadImageData),
      inspiration:
        inspirationMood && inspirationPrompt
          ? {
              mood: inspirationMood,
              prompt: inspirationPrompt,
            }
          : undefined,
    })

    logInfo('NewBoardTemplatePage', 'Starting staged board creation', {
      boardId,
      imageCount: selectedImages.length,
    })

    setIsMakingBoard(true)
    navigate(
      `/board/${boardId}?template=blank${inspirationMood ? `&mood=${encodeURIComponent(inspirationMood)}` : ''}${inspirationPrompt ? `&prompt=${encodeURIComponent(inspirationPrompt)}` : ''}`
    )
  }

  const handleSkip = () => {
    const boardId = crypto.randomUUID()

    logInfo(
      'NewBoardTemplatePage',
      'Skipping staged images and creating blank board',
      {
        boardId,
      }
    )

    setIsMakingBoard(true)
    stageNewBoardPayload(boardId, {
      images: [],
      inspiration:
        inspirationMood && inspirationPrompt
          ? {
              mood: inspirationMood,
              prompt: inspirationPrompt,
            }
          : undefined,
    })
    navigate(
      `/board/${boardId}?template=blank${inspirationMood ? `&mood=${encodeURIComponent(inspirationMood)}` : ''}${inspirationPrompt ? `&prompt=${encodeURIComponent(inspirationPrompt)}` : ''}`
    )
  }

  return (
    <div className="min-h-screen bg-background-50 pb-app-content-enter">
      <MetaTags
        title="New Board | Printboard"
        description="Start a new board and make your vision feel closer."
        noindex={true}
      />
      <PageContainer
        maxWidth="full"
        className="flex min-h-screen items-start px-5 pb-12 pt-[calc(env(safe-area-inset-top,0px)+1.5rem)] md:items-center md:justify-center md:px-10 md:py-12"
      >
        <div className="mx-auto flex w-full max-w-[880px] flex-col items-start md:items-center">
          <div className="w-full max-w-[560px] text-left md:max-w-[520px] md:text-center">
            <h1 className="font-serif text-fs-700 font-semibold tracking-tight text-ink-700">
              Start with a few images.
            </h1>
            <Text
              as="p"
              variant="body"
              className="mt-3 max-w-[440px] text-ink-500 md:mx-auto md:mt-4 md:max-w-[420px]"
            >
              Share a few images you love and we&apos;ll make the first board
              for you.
            </Text>
          </div>

          <div className="mt-8 w-full max-w-[720px] md:mt-14">
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  fileInputRef.current?.click()
                }
              }}
              onDragEnter={(event) => {
                if (!isFileDrag(event.dataTransfer)) return
                event.preventDefault()
                setDragActive(true)
              }}
              onDragOver={(event) => {
                if (!isFileDrag(event.dataTransfer)) return
                event.preventDefault()
                event.dataTransfer.dropEffect = 'copy'
                setDragActive(true)
              }}
              onDragLeave={(event) => {
                event.preventDefault()
                const relatedTarget = event.relatedTarget
                if (
                  relatedTarget instanceof Node &&
                  event.currentTarget.contains(relatedTarget)
                ) {
                  return
                }
                setDragActive(false)
              }}
              onDrop={(event) => void handleDrop(event)}
              className={[
                'group relative flex cursor-pointer flex-col items-center justify-center rounded-[28px] border border-[rgba(36,34,30,0.08)] bg-background-50 text-center transition-colors duration-settle',
                hasSelectedImages
                  ? 'min-h-[120px] px-5 py-5 md:min-h-[168px] md:px-8 md:py-7'
                  : 'min-h-[220px] px-5 py-8 md:min-h-[260px] md:px-8 md:py-12',
                dragActive
                  ? 'border-primary-300 bg-background-100'
                  : 'hover:border-[rgba(36,34,30,0.14)] hover:bg-background-100/65',
                isMakingBoard ? 'pointer-events-none opacity-70' : '',
              ].join(' ')}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                multiple
                className="sr-only"
                onChange={(event) => void handleInputChange(event)}
              />

              <div
                className={[
                  'flex items-center justify-center rounded-full bg-background-100 text-primary-300',
                  hasSelectedImages
                    ? 'h-10 w-10 md:h-11 md:w-11'
                    : 'h-12 w-12 md:h-14 md:w-14',
                ].join(' ')}
              >
                <UploadSimple
                  className={
                    hasSelectedImages
                      ? 'h-4 w-4 md:h-5 md:w-5'
                      : 'h-5 w-5 md:h-6 md:w-6'
                  }
                  weight="regular"
                />
              </div>

              <p
                className={[
                  'font-serif font-semibold tracking-tight text-ink-700',
                  hasSelectedImages
                    ? 'mt-3 text-fs-400 md:mt-4 md:text-fs-600'
                    : 'mt-5 text-fs-600 md:mt-6',
                ].join(' ')}
              >
                {hasSelectedImages ? 'Add more images' : 'Drop images here'}
              </p>
              <p
                className={[
                  'font-sans text-ink-500',
                  hasSelectedImages
                    ? 'mt-1 text-fs-300 md:mt-2'
                    : 'mt-2 text-fs-400 md:mt-3',
                ].join(' ')}
              >
                {hasSelectedImages
                  ? 'Keep building your board with a few more from your device.'
                  : 'Or click to upload from your device.'}
              </p>
              <p
                className={[
                  'font-sans text-fs-300 text-ink-400',
                  hasSelectedImages ? 'mt-2 md:mt-3' : 'mt-3 md:mt-4',
                ].join(' ')}
              >
                {isUploading ? 'Uploading...' : helperCopy}
              </p>
            </div>

            {uploadFeedback ? (
              <div
                className={[
                  'mt-4 rounded-[18px] px-4 py-3 text-center font-sans text-fs-300',
                  uploadFeedback.className,
                ].join(' ')}
                aria-live="polite"
              >
                {uploadFeedback.message}
              </div>
            ) : null}

            {hasSelectedImages ? (
              <div className="mt-8 w-full">
                <div className="mb-6 flex flex-col items-center px-1 py-1 text-center">
                  <Button
                    variant="primary"
                    size="lg"
                    loading={isMakingBoard}
                    disabled={!canMakeBoard}
                    onClick={handleMakeBoard}
                    className="w-full max-w-[280px]"
                  >
                    Make Board
                  </Button>

                  <button
                    type="button"
                    onClick={handleSkip}
                    disabled={isMakingBoard}
                    className="mt-4 font-sans text-fs-300 text-ink-400 transition-colors duration-settle hover:text-ink-500 focus:outline-none"
                  >
                    Skip this
                  </button>
                </div>
                <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {selectedImages.map((image) => (
                    <div
                      key={image.storagePath}
                      className="group relative aspect-[4/5] min-w-0 overflow-hidden rounded-[20px] bg-background-100"
                    >
                      <img
                        src={image.signedUrl}
                        alt={image.originalFileName || 'Selected upload'}
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          setSelectedImages((prev) =>
                            prev.filter(
                              (item) => item.storagePath !== image.storagePath
                            )
                          )
                          setFeedbackMessage('')
                        }}
                        className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[rgba(36,34,30,0.08)] bg-background-50/96 text-ink-600 shadow-[0_8px_24px_rgba(36,34,30,0.12)] transition-opacity duration-settle hover:text-ink-700 focus:outline-none md:h-8 md:w-8 md:opacity-0 md:shadow-none md:group-hover:opacity-100 md:focus:opacity-100"
                        aria-label={`Remove ${image.originalFileName || 'image'}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div
            className={[
              'w-full max-w-[720px]',
              hasSelectedImages ? 'mt-8 md:mt-10' : 'mt-12',
            ].join(' ')}
          >
            {!hasSelectedImages ? (
              <div className="mx-auto flex w-full max-w-[720px] flex-col items-center">
                <Button
                  variant="primary"
                  size="lg"
                  loading={isMakingBoard}
                  disabled={!canMakeBoard}
                  onClick={handleMakeBoard}
                  className="min-w-[180px]"
                >
                  Make Board
                </Button>

                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={isMakingBoard}
                  className="mt-8 font-sans text-fs-300 text-ink-400 transition-colors duration-settle hover:text-ink-500 focus:outline-none"
                >
                  Skip this
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </PageContainer>
    </div>
  )
}

export default NewBoardTemplatePage
